import {FormConfig} from "../../form/form.config";
import {FormModel} from "../../form/form.model.abstract";
import {FormModelRadio} from "../../form/form.model.radio";
import {FormGroup, ValidatorFn, Validators} from "@angular/forms";
import {PhotovoltaikcheckAnfrage} from "../anfrage/photovoltaikcheck.anfrage";
import {FormModelText} from "../../form/form.model.text";
import {ValidationService} from "../../services/validation.service";
import {PhotovoltaikcheckConstants} from "../photovoltaikcheck.constants";
import {FormModelRoofpitch} from "../../form/form.model.roofpitch";
import {FormModelDirection} from "../../form/form.model.direction";
import {FormModelNumber} from "../../form/form.model.number";

/**
 * Configuratuion for the input of the first chapter in the foerdermittelcheck advisor.
 * @inheritDoc
 */

export class EingabeForm extends FormConfig {
  /**
   * some data is needed here for validation
   */
  public anfrage: PhotovoltaikcheckAnfrage;

  /**
   * @inheritDoc
   * @protected
   *
   */
  protected fields: FormModel[] = [
    // basisdaten
    new FormModelRadio('nutzungstyp', [], [Validators.required], null, null, false),
    new FormModelRadio('gebaeudetyp', [PhotovoltaikcheckConstants.GEBAEUDETYP_EFH, PhotovoltaikcheckConstants.GEBAEUDETYP_MFH],
      [Validators.required], null, null, false),
    (new FormModelText('postleitzahl', [Validators.required, ValidationService.postalCode], null, null, false)).setClasses('small-field'),

    // stromverbrauch
    new FormModelRadio('waermepumpe', ['vorhanden', 'geplant', 'nein'], [Validators.required], null, null, false),
    new FormModelRadio('elektroauto', ['vorhanden', 'geplant', 'nein'], [Validators.required], null, null, false),
    (new FormModelText('stromverbrauch_pro_jahr', [Validators.required, ValidationService.greaterZero],
      'kwh', null, false, 'number')).setCleanValue(true),

    // aufstellort
    (new FormModelRadio('aufstellort', [], [Validators.required], null, null, false)).setIconPath('/pvc/icon-gebaeude_dachform_'),
    new FormModelDirection('himmelsausrichtung', [Validators.required], 'grad', null, false),
    // tslint:disable-next-line:max-line-length
    (new FormModelRoofpitch('dachneigung', [Validators.required], 'grad', PhotovoltaikcheckConstants.VALIDATE_DACHNEIGUNG_DEFAULT, false)).setInactive(),
    // tslint:disable-next-line:max-line-length
    new FormModelText('flaeche_verfuegbar', [Validators.required, Validators.min(PhotovoltaikcheckConstants.VALIDATE_FLAECHE_VERFUEGBAR_MIN), Validators.max(PhotovoltaikcheckConstants.VALIDATE_FLAECHE_VERFUEGBAR_MAX)], 'm2', null, false, 'number'),
    (new FormModelText('wohnflaeche', [Validators.required], 'm2', null, false, 'number')).setInactive(),
    // tslint:disable-next-line:max-line-length
    (new FormModelNumber('stockwerke', [Validators.required, Validators.min(PhotovoltaikcheckConstants.VALIDATE_STOCKWERKE_MIN), Validators.max(PhotovoltaikcheckConstants.VALIDATE_STOCKWERKE_MAX), ValidationService.allowIntegerOnly], null, 1, false)).setInactive(),
  ];

  /**
   * List of fields that must be de/activated on form changes
   */
  fieldsForFormChanges: string[] = ['dachneigung'];

  /**
   * @inheritDoc
   * @protected
   */
  protected formValidators = [
    EingabeForm.consumptionMax(this)
  ];

  /**
   * @inheritDoc
   */
  formValidatorsFields = {consumptionMax: ['stromverbrauch_pro_jahr']};

  /**
   * collection of options for some fields
   */
  options = {
    nutzungstyp: {
      photovoltaikanlage: [PhotovoltaikcheckConstants.NUTZUNGSTYP_HAUSEIGENTUEMER,
        PhotovoltaikcheckConstants.NUTZUNGSTYP_WOHNUNGSEIGENTUEMER, PhotovoltaikcheckConstants.NUTZUNGSTYP_MIETER,
        PhotovoltaikcheckConstants.NUTZUNGSTYP_HAUSVERWALTER],
      balkonkraftwerk: [PhotovoltaikcheckConstants.NUTZUNGSTYP_HAUSEIGENTUEMER, PhotovoltaikcheckConstants.NUTZUNGSTYP_WOHNUNGSEIGENTUEMER,
        PhotovoltaikcheckConstants.NUTZUNGSTYP_MIETER]
    },
    aufstellort: {
      photovoltaikanlage: [PhotovoltaikcheckConstants.AUFSTELLORT_SPITZDACH, PhotovoltaikcheckConstants.AUFSTELLORT_PULTDACH,
        PhotovoltaikcheckConstants.AUFSTELLORT_FLACHDACH, PhotovoltaikcheckConstants.AUFSTELLORT_FASSADE],
      balkonkraftwerk: [PhotovoltaikcheckConstants.AUFSTELLORT_BALKON, PhotovoltaikcheckConstants.AUFSTELLORT_FREIFLAECHE]
    }
  };

  /**
   * @inheritDoc
   * @protected
   */
  protected groups: any = {
    'basisdaten/1': ['nutzungstyp'],
    'basisdaten/2': ['gebaeudetyp'],
    'basisdaten/3': ['postleitzahl'],
    'stromverbrauch/1': ['waermepumpe'],
    'stromverbrauch/2': ['elektroauto'],
    'stromverbrauch/3': ['stromverbrauch_pro_jahr'],
    'aufstellort/1': ['aufstellort', 'dachneigung'],
    'aufstellort/2': ['himmelsausrichtung'],
    'aufstellort/3': ['flaeche_verfuegbar', 'wohnflaeche', 'stockwerke']
  };

  /**
   * Validator for the energy consumption
   * @param formConfig
   */
  static consumptionMax(formConfig: EingabeForm): ValidatorFn {
    return(formGroup: FormGroup) => {
      // check if there is a value in the field 'stromverbrauch_pro_jahr'
      if (formGroup?.controls?.stromverbrauch_pro_jahr?.value && formConfig.anfrage) {
        const value = formGroup.controls.stromverbrauch_pro_jahr.value;
        const max = formConfig.anfrage.getMaxConsumption();
        if (value > max) {
          return {consumptionMax: false};
        }
      }
      return null;
    };
  }

  /**
   * Any time a field is updated, this method is called.
   * @param values
   */
  updateFields(values: { [key: string]: any }): any {
    // set options of 'nutzetyp' and 'aufstellort' depending on the path
    if(this.anfrage.interesse) {
      this.setOptions('nutzungstyp', this.options.nutzungstyp[this.anfrage.interesse]);
      this.setOptions('aufstellort', this.options.aufstellort[this.anfrage.interesse]);

      // reset values, if the user was in another path before
      if(values.hasOwnProperty('nutzungstyp') && this.options.nutzungstyp[this.anfrage.interesse].indexOf(values.nutzungstyp) === -1) {
        values.nutzungstyp = null;
      }
      if(values.hasOwnProperty('aufstellort') && this.options.aufstellort[this.anfrage.interesse].indexOf(values.aufstellort) === -1) {
        values.aufstellort = null;
      }
    }

    // de/activate fields, depending on the path
    if(this.anfrage.interesse === PhotovoltaikcheckConstants.PATH_PVANLAGE) {
      // photovoltaikanlage
      this.activateField('waermepumpe');
      this.activateField('elektroauto');
      this.activateField('dachneigung');
      this.activateField('flaeche_verfuegbar');
    }
    else {
      // balkonkraftwerk
      this.deactivateField('waermepumpe');
      this.deactivateField('elektroauto');
      this.deactivateField('dachneigung');
      this.deactivateField('flaeche_verfuegbar');
      values.waermepumpe = null;
      values.elektroauto = null;
      values.dachneigung = null;
      values.flaeche_verfuegbar = null;
    }

    // de/activate some fields, depending on the user.
    if(values.hasOwnProperty('nutzungstyp')) {
      if(values.nutzungstyp === 'wohnungseigentuemer') {
        this.deactivateField('gebaeudetyp');
        values.gebaeudetyp = PhotovoltaikcheckConstants.GEBAEUDETYP_MFH;
      }
      else {
        this.activateField('gebaeudetyp');
      }
    }

    // de/activate some fields, depending on the building.
    if(values.hasOwnProperty('gebaeudetyp')) {
      if(values.gebaeudetyp === PhotovoltaikcheckConstants.GEBAEUDETYP_EFH &&
        this.anfrage.interesse !== PhotovoltaikcheckConstants.PATH_BALKONKRAFTWERK) {
        this.activateField('waermepumpe');
        this.activateField('elektroauto');
      }
      else {
        this.deactivateField('waermepumpe');
        this.deactivateField('elektroauto');
        values.waermepumpe = null;
        values.elektroauto = null;
      }

      // no request for consumptions on mfh/solar
      if(values.gebaeudetyp === PhotovoltaikcheckConstants.GEBAEUDETYP_MFH &&
        this.anfrage.interesse === PhotovoltaikcheckConstants.PATH_PVANLAGE) {
        this.deactivateField('stromverbrauch_pro_jahr');
        values.stromverbrauch_pro_jahr = null;
      }
      else {
        this.activateField('stromverbrauch_pro_jahr');
      }
    }

    // de/activate fields depending on the aufstellort
    if(values.hasOwnProperty('aufstellort')) {
      // flachdach
      if(values.aufstellort === PhotovoltaikcheckConstants.AUFSTELLORT_FLACHDACH ||
         values.aufstellort === PhotovoltaikcheckConstants.AUFSTELLORT_FREIFLAECHE) {
        this.deactivateField('himmelsausrichtung');
        values.himmelsausrichtung = null;
      }
      else {
        this.activateField('himmelsausrichtung');
      }

      // spitzdach
      if(values.aufstellort === PhotovoltaikcheckConstants.AUFSTELLORT_SPITZDACH ||
         values.aufstellort === PhotovoltaikcheckConstants.AUFSTELLORT_PULTDACH ) {
        this.activateField('dachneigung');

        // set the fieldtype
        this.getField('dachneigung').setFieldType(values.aufstellort === PhotovoltaikcheckConstants.AUFSTELLORT_SPITZDACH ?
          FormModelRoofpitch.TYPE_PITCH : FormModelRoofpitch.TYPE_PENT);
        (this.getField('himmelsausrichtung') as FormModelDirection).setRoofType(
          values.aufstellort === PhotovoltaikcheckConstants.AUFSTELLORT_PULTDACH ?
            FormModelDirection.ROOF_TYPE_PENT : FormModelDirection.ROOF_TYPE_HALF
        );
      }
      else {
        this.deactivateField('dachneigung');
        values.dachneigung = null;

        // fassade, freiflaeche
        if(values.aufstellort === PhotovoltaikcheckConstants.AUFSTELLORT_FASSADE ||
          values.aufstellort === PhotovoltaikcheckConstants.AUFSTELLORT_BALKON) {
          (this.getField('himmelsausrichtung') as FormModelDirection).setRoofType(FormModelDirection.ROOF_TYPE_FULL);
        }
      }
    }

    return values;
  }

  /**
   * @inheritDoc
   * @protected
   */
  protected onChange() {
    // check if the form has some fields which value changes other fields.
    if(this.fieldsForFormChanges.find(field => this.form.get(field)) !== undefined) {
      this.updateFields(this.getData());
    }
  }
}
